import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { DownloadSimple, Calendar, ArrowRight } from "@phosphor-icons/react";
import { fmtDate } from "@/lib/constants";

// Buyer/client-facing downloads page. Clean, purpose-built for pulling lead
// exports without the full operator UI. Internal ops can still use it too;
// the same server-side scope guard applies (client_admins only see their own
// leads regardless of what they type into the form).

const STATUSES = [
  { value: "", label: "Any" },
  { value: "validated", label: "Validated" },
  { value: "delivered", label: "Delivered" },
  { value: "duplicate", label: "Duplicate (returned)" },
];

function toISOStart(d) { return d ? `${d}T00:00:00Z` : ""; }
function toISOEnd(d) { return d ? `${d}T23:59:59Z` : ""; }

export default function Downloads() {
  const { user } = useAuth();
  const isClient = user?.org_type === "client";
  const canExport = user?.permissions?.includes("export_leads") || user?.role === "super_admin";

  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [status, setStatus] = useState("");
  const [testOnly, setTestOnly] = useState("");
  const [fmt, setFmt] = useState("csv");   // csv | xlsx — buyer picks format
  const [count, setCount] = useState(null);   // matching leads pre-download
  const [busy, setBusy] = useState(false);
  const [recent, setRecent] = useState([]);   // recent audit rows for "your downloads"

  // Preview count so the buyer knows how many rows they're about to pull.
  const query = useMemo(() => {
    const p = new URLSearchParams();
    if (status) p.set("status", status);
    if (testOnly) p.set("is_test", testOnly);
    if (dateFrom) p.set("date_from", toISOStart(dateFrom));
    if (dateTo)   p.set("date_to",   toISOEnd(dateTo));
    return p;
  }, [status, testOnly, dateFrom, dateTo]);

  useEffect(() => {
    // Ask the server how many rows would come back with the current filter.
    const p = new URLSearchParams(query);
    p.set("limit", "1");
    api.get(`/leads?${p.toString()}`)
      .then((r) => setCount(r.data.total))
      .catch(() => setCount(null));
  }, [query]);

  useEffect(() => {
    // Recent export audit entries for this user — nice trust signal for buyers.
    api.get("/audit?action=leads_exported&limit=10")
      .then((r) => setRecent(r.data?.audit || r.data?.entries || r.data || []))
      .catch(() => setRecent([]));
  }, []);

  const preset = (days) => {
    const to = new Date();
    const from = new Date();
    from.setDate(to.getDate() - days + 1);
    const iso = (d) => d.toISOString().slice(0, 10);
    setDateFrom(iso(from));
    setDateTo(iso(to));
  };

  const download = async () => {
    if (!canExport) { toast.error("You do not have permission to export leads"); return; }
    setBusy(true);
    try {
      const p = new URLSearchParams(query);
      p.set("max_rows", "50000");
      const mime = fmt === "xlsx"
        ? "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        : "text/csv";
      const resp = await api.get(`/leads/export.${fmt}?${p.toString()}`, { responseType: "blob" });
      const exported = resp.headers?.["x-rows-exported"] || resp.headers?.["X-Rows-Exported"];
      const filename = (resp.headers?.["content-disposition"] || "").split("filename=")[1]?.replace(/"/g, "")
                      || `leadflow-leads.${fmt}`;
      const blob = new Blob([resp.data], { type: mime });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = filename;
      link.click();
      URL.revokeObjectURL(link.href);
      toast.success(`Downloaded ${exported || count || "your"} leads (${fmt.toUpperCase()})`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Export failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <div className="mb-6">
        <div className="label-mono">Downloads</div>
        <h1 className="font-chivo font-black text-4xl md:text-5xl uppercase tracking-tighter leading-none">
          Lead Downloads
        </h1>
        <p className="mt-2 text-sm text-zinc-600 max-w-2xl">
          {isClient
            ? "Pull all leads delivered to your organization. Choose a date range and any additional filters, then click Download CSV. Files open cleanly in Excel, Google Sheets, or any CRM that imports CSV."
            : "Export the full lead ledger, filtered by date, status, or client. Rows are streamed as CSV — no row-cap on production data up to 50,000 leads per request."}
        </p>
      </div>

      <div className="brut-card p-6 space-y-6" data-testid="downloads-panel">
        {/* Date range with visible presets — the single most-asked feature from buyers. */}
        <div>
          <div className="flex items-center gap-2 mb-2">
            <Calendar size={16} weight="bold" />
            <span className="label-mono">Date range</span>
            <span className="ml-2 flex gap-1">
              {[
                { label: "Today", days: 1 },
                { label: "Last 7 days",  days: 7 },
                { label: "Last 30 days", days: 30 },
                { label: "Last 90 days", days: 90 },
              ].map((r) => (
                <button key={r.label} onClick={() => preset(r.days)}
                  data-testid={`downloads-preset-${r.days}`}
                  className="border border-black px-2 py-1 text-[10px] uppercase tracking-widest hover:bg-black hover:text-white">
                  {r.label}
                </button>
              ))}
              {(dateFrom || dateTo) && (
                <button onClick={() => { setDateFrom(""); setDateTo(""); }}
                  data-testid="downloads-preset-clear"
                  className="border border-zinc-500 text-zinc-600 px-2 py-1 text-[10px] uppercase tracking-widest hover:bg-zinc-200">
                  Clear
                </button>
              )}
            </span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div>
              <label className="label-mono">From</label>
              <input type="date" data-testid="downloads-date-from"
                className="w-full border-2 border-black px-2 py-1.5 mt-1 font-mono"
                value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
            </div>
            <div>
              <label className="label-mono">To</label>
              <input type="date" data-testid="downloads-date-to"
                className="w-full border-2 border-black px-2 py-1.5 mt-1 font-mono"
                value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
            </div>
            <div>
              <label className="label-mono">Status</label>
              <select data-testid="downloads-status"
                className="w-full border-2 border-black px-2 py-1.5 mt-1 font-mono"
                value={status} onChange={(e) => setStatus(e.target.value)}>
                {STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </div>
            <div>
              <label className="label-mono">Test / Prod</label>
              <select data-testid="downloads-testprod"
                className="w-full border-2 border-black px-2 py-1.5 mt-1 font-mono"
                value={testOnly} onChange={(e) => setTestOnly(e.target.value)}>
                <option value="">Any</option>
                <option value="false">Production only</option>
                <option value="true">Test only</option>
              </select>
            </div>
          </div>

          <div className="mt-4 flex items-center gap-4 border-t border-zinc-300 pt-4">
            <span className="label-mono">Format</span>
            <label className={`inline-flex items-center gap-2 border-2 border-black px-3 py-1.5 cursor-pointer ${fmt === "csv" ? "bg-black text-white" : "hover:bg-zinc-100"}`}
              data-testid="downloads-format-csv">
              <input type="radio" name="fmt" value="csv" checked={fmt === "csv"}
                onChange={() => setFmt("csv")} className="hidden" />
              <span className="text-xs uppercase tracking-widest font-bold">CSV</span>
              <span className="text-[10px] font-mono opacity-70">.csv — universal</span>
            </label>
            <label className={`inline-flex items-center gap-2 border-2 border-black px-3 py-1.5 cursor-pointer ${fmt === "xlsx" ? "bg-black text-white" : "hover:bg-zinc-100"}`}
              data-testid="downloads-format-xlsx">
              <input type="radio" name="fmt" value="xlsx" checked={fmt === "xlsx"}
                onChange={() => setFmt("xlsx")} className="hidden" />
              <span className="text-xs uppercase tracking-widest font-bold">XLSX</span>
              <span className="text-[10px] font-mono opacity-70">.xlsx — Excel / Sheets</span>
            </label>
          </div>
        </div>

        {/* Preview + Download */}
        <div className="flex items-center justify-between border-t border-zinc-300 pt-4">
          <div className="text-sm">
            <div className="label-mono">Matching leads</div>
            <div className="font-chivo font-black text-3xl" data-testid="downloads-match-count">
              {count === null ? "—" : count.toLocaleString()}
            </div>
            <div className="text-[11px] text-zinc-500 mt-1">
              Max 50,000 rows per download. Adjust date range to narrow if higher.
            </div>
          </div>
          <button onClick={download} disabled={busy || !canExport || count === 0}
            data-testid="downloads-submit"
            className="bg-black text-white border-2 border-black px-6 py-3 text-xs uppercase tracking-widest inline-flex items-center gap-3
                       hover:translate-x-[2px] hover:translate-y-[2px] hover:brut-shadow-sm transition-transform duration-100
                       disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:transform-none">
            {busy ? "Preparing…" : <><DownloadSimple size={16} weight="bold" /> Download {fmt.toUpperCase()} <ArrowRight size={14} /></>}
          </button>
        </div>
      </div>

      {/* Recent export history — trust + auditability for the buyer. */}
      {recent.length > 0 && (
        <div className="brut-card mt-4 p-6" data-testid="downloads-recent">
          <div className="font-chivo font-bold uppercase tracking-tight mb-3">Recent downloads</div>
          <div className="overflow-x-auto">
          <table className="w-full text-xs font-mono min-w-[720px]">
            <thead>
              <tr className="border-b border-black text-[10px] uppercase tracking-widest">
                <th className="py-2 text-left">When</th>
                <th className="py-2 text-left">By</th>
                <th className="py-2 text-right">Rows</th>
                <th className="py-2 text-left">Filters</th>
              </tr>
            </thead>
            <tbody>
              {recent.slice(0, 10).map((r, i) => {
                const filters = r.after?.filters || {};
                const filterBits = Object.entries(filters)
                  .filter(([, v]) => v !== null && v !== undefined && v !== "")
                  .map(([k, v]) => `${k}=${typeof v === "string" ? v.slice(0, 22) : v}`);
                const when = r.timestamp || r.created_at;
                const by = r.actor_email || r.actor_name || "—";
                const rows = r.after?.row_count;
                return (
                  <tr key={r.id || i} className="border-b border-zinc-200">
                    <td className="py-1.5 pr-4">{fmtDate(when)}</td>
                    <td className="py-1.5 pr-4">{by}</td>
                    <td className="py-1.5 pr-4 text-right">{typeof rows === "number" ? rows.toLocaleString() : "—"}</td>
                    <td className="py-1.5 text-zinc-600">{filterBits.join(" · ") || "(no filter)"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>
        </div>
      )}
    </div>
  );
}
